import { useState, useEffect } from 'react';
import { ArrowLeft, AlertCircle, Clock, TrendingUp, TrendingDown, Minus, Loader2, Shield, ChevronRight } from 'lucide-react';
import { apiGet } from '@/services/apiClient';
import { useTeacherNavigation } from '@/contexts/TeacherNavigationContext';
import { useTeacherTheme } from '@/contexts/TeacherThemeContext';
import type { MonitoredStudent } from '@/services/dashboardService';

interface StudentProfileModalProps {
  student: MonitoredStudent;
  onClose: () => void;
}

interface StudentAlert {
  id: string;
  title: string;
  type: string;
  severity: string;
  status: string;
  createdAt: string;
}

export function StudentProfileModal({ student, onClose }: StudentProfileModalProps) {
  const { setCurrentPage } = useTeacherNavigation();
  const { dm } = useTeacherTheme();
  const [alerts, setAlerts] = useState<StudentAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudentAlerts = async () => {
      try {
        const response = await apiGet<{ data: StudentAlert[]; total: number }>(
          `/alerts?page=1&limit=20`
        );
        const studentAlerts = (response.data || []).filter(
          (a: StudentAlert & { studentName?: string; student?: { name: string } }) => a.studentName === student.name || a.student?.name === student.name
        );
        setAlerts(studentAlerts);
      } catch {
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStudentAlerts();
  }, [student.name]);

  const formatTimeAgo = (dateString: string) => {
    try {
      const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
      if (seconds < 60) return 'Just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    } catch { return 'Unknown'; }
  };

  const riskColor = (score: number) => {
    if (score >= 70) return { bar: 'bg-red-500', text: 'text-red-600', label: 'High Risk', badge: 'bg-red-100 text-red-700 border border-red-200' };
    if (score >= 40) return { bar: 'bg-amber-400', text: 'text-amber-600', label: 'Medium Risk', badge: 'bg-amber-100 text-amber-700 border border-amber-200' };
    return { bar: 'bg-emerald-400', text: 'text-emerald-600', label: 'Low Risk', badge: 'bg-emerald-100 text-emerald-700 border border-emerald-200' };
  };

  const severityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': case 'high': return 'bg-red-100 text-red-700 border border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-700 border border-orange-200';
      default: return 'bg-blue-100 text-blue-700 border border-blue-200';
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'new': return dm ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
      case 'resolved': return dm ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700';
      case 'escalated': return dm ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700';
      case 'dismissed': return dm ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-600';
      default: return dm ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700';
    }
  };

  const handleAlertClick = (alertId: string) => {
    // Navigate to alerts page
    setCurrentPage('alerts');
    // Tell ActiveAlerts to open this specific alert
    window.dispatchEvent(new CustomEvent('teacher-open-alert', { detail: { alertId } }));
  };

  const risk = riskColor(student.riskScore);
  const initials = student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="max-w-3xl mx-auto space-y-4">

      {/* Back button */}
      <button
        onClick={onClose}
        className="flex items-center gap-2 text-sm font-medium transition-colors"
        style={{ 
          background: 'none', 
          border: 'none', 
          cursor: 'pointer', 
          padding: 0,
          color: dm ? '#e2e8f0' : '#000000'
        }}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Students
      </button>

      {/* Profile header card */}
      <div className={`border rounded-xl p-6 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center gap-4">
          <div className={`h-16 w-16 rounded-full flex items-center justify-center text-xl font-bold shrink-0 ${risk.badge.includes('red') ? 'bg-red-100 text-red-700' : risk.badge.includes('amber') ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-900'}`}>{student.name}</h2>
            <p className={`text-sm truncate ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{student.email || 'No email on file'}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${risk.badge}`}>
            {risk.label}
          </span>
        </div>
      </div>

      {/* Risk score card */}
      <div className={`border rounded-xl p-5 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-semibold ${dm ? 'text-slate-300' : 'text-slate-700'}`}>Risk Score</span>
          <span className={`text-2xl font-bold ${risk.text}`}>{student.riskScore}%</span>
        </div>
        <div className={`w-full rounded-full h-2.5 mb-4 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <div
            className={`h-2.5 rounded-full ${risk.bar} transition-all`}
            style={{ width: `${Math.min(student.riskScore, 100)}%` }}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className={`text-center p-3 rounded-lg ${dm ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{student.alertsThisMonth ?? 0}</p>
            <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Alerts this month</p>
          </div>
          <div className={`text-center p-3 rounded-lg ${dm ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-sm font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>
              {student.lastActive ? formatTimeAgo(student.lastActive) : '—'}
            </p>
            <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Last activity</p>
          </div>
          <div className={`text-center p-3 rounded-lg flex flex-col items-center ${dm ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            {student.riskScore >= 70
              ? <TrendingUp className="h-5 w-5 text-red-500" />
              : student.riskScore >= 40
              ? <Minus className="h-5 w-5 text-amber-400" />
              : <TrendingDown className="h-5 w-5 text-emerald-500" />}
            <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Trend</p>
          </div>
        </div>
      </div>

      {/* Recent alerts card */}
      <div className={`border rounded-xl shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className={`flex items-center gap-2 px-5 py-4 border-b ${dm ? 'border-slate-700' : 'border-slate-100'}`}>
          <AlertCircle className="h-4 w-4 text-red-400" />
          <h3 className={`text-sm font-semibold ${dm ? 'text-slate-200' : 'text-slate-700'}`}>Recent Alerts</h3>
          <span className={`ml-auto text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Click any alert to open it</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin h-5 w-5 text-blue-500" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center py-10 gap-2">
            <Shield className="h-7 w-7 text-emerald-300" />
            <p className={`text-sm ${dm ? 'text-slate-500' : 'text-slate-400'}`}>No recent alerts for this student</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {alerts.map(alert => (
              <button
                key={alert.id}
                onClick={() => handleAlertClick(alert.id)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors group ${
                  dm
                    ? 'hover:bg-slate-700/60'
                    : 'hover:bg-slate-50'
                }`}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                    {(alert.title || alert.type)?.replace(/_/g, ' ')}
                  </p>
                  <div className={`flex items-center gap-1 mt-0.5 text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(alert.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${severityBadge(alert.severity)} capitalize`}>
                    {alert.severity}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(alert.status)}`}>
                    {alert.status}
                  </span>
                  <ChevronRight className={`h-4 w-4 transition-transform group-hover:translate-x-0.5 ${dm ? 'text-slate-600' : 'text-slate-300'}`} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
